import moment from 'moment';
import { func, node, oneOfType, shape, string } from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import DevMode, {
  isDevMode as isDevOnlyMode,
  isTestMode,
} from '~/components/DevMode';
import MFEBootstrapTool from '~/components/MFEBootstrapTool';
import FullStoryPageChangeTracker from '~/components/MFEProvider/lib/ReactRouterDom/PageChangeTracker';
import useIsHalGUI from '~/hooks/useIsHalGUI';
import useLocalStorage from '~/hooks/useLocalStorage';
import { getHostingRenewalStatus } from '~/scenes/Hosting/components/RenewalSettings/utils/helpers';
import { extractJWTBrand } from '~/utils/brand/helpers';
import { YYYY_MMMM_DD_HMS } from '~/utils/date/constants';

import AnalyticsProvider from './Analytics';
import ErrorBoundary from './ErrorBoundary';
import HttpClient from './HttpClient';
import i18n from './I18N';
import MUI from './MUI';
import ReactRouterDom from './ReactRouterDom';
import ReactSuspense from './ReactSuspense';

const MFEContext = createContext({});
export const useMFEContext = () => useContext(MFEContext);

const propTypes = {
  children: oneOfType([node, func]).isRequired,
  basename: string, // TODO: this may be required
  errorBoundaryConfig: shape({}),
  reactQueryConfig: shape({}),
  muiConfig: shape({}),
  data: shape({}),
  callbacks: shape({}),
};

export const MFEProvider = ({
  children,
  basename = '',
  errorBoundaryConfig = undefined,
  reactQueryConfig = undefined,
  muiConfig = undefined,
  hasAnalytics = true,
  data = {},
  callbacks = {},
}) => {
  const { t } = useTranslation('common');
  // Leave this useState in MFEProvider
  const [accessToken, setAccessToken] = useState(data?.accessToken);
  const [banners, setBanners] = useState([]);
  /**
   * brand as encoded in jwt token
   */
  const brandFromJWT = extractJWTBrand(accessToken);

  /**
   * formatted brand name, ex: "Bluehost" or "HostGator"
   */
  const brand = t(`brand.${brandFromJWT}`, {
    defaultValue: '', // if no brand value is found in translation then we return an empty string
  });

  const isRevampUI = data?.isRevampUI;
  const isHalGui = useIsHalGUI();
  const normalizedBrand = brand?.toLowerCase();
  const isBlueHost = normalizedBrand === 'bluehost';
  const isHostgator = normalizedBrand === 'hostgator';
  const isAMRevamp = !isHalGui && (isBlueHost || isHostgator) && isRevampUI;

  const isDevMode = isDevOnlyMode && !isTestMode;
  const isDevOrTestMode = isDevOnlyMode || isTestMode;

  // in order to avoid prop-drilling as soon as we receive these props, they get put into the context
  // and they all will be accessible from any component within the MFE application
  const providerData = useMemo(
    () => ({
      ...((isDevOrTestMode || !isAMRevamp) && { accessToken }), // local state
      brand,
      brandFromJWT,
      data: {
        ...data,
        ...(!isDevOrTestMode && isAMRevamp && { accessToken: '' }),
      },
      callbacks,
      hostingId: data?.hostingId,
      hostingStatus: {
        renewalStatus: getHostingRenewalStatus(data?.hostingData?.expiryDate),
        isActive: data?.hostingData?.status?.toLowerCase() === 'active',
        isProvisioning:
          data?.hostingData?.status?.toLowerCase() === 'provisioning',
      },
    }),
    [
      accessToken,
      brand,
      brandFromJWT,
      callbacks,
      data,
      isAMRevamp,
      isDevOrTestMode,
    ],
  );

  const localStorageKey = `HOST_APP_PROPS`;
  const [lsHostAppData, setLSHostAppData, removeItem] = useLocalStorage(
    localStorageKey, // local storage key
    providerData, // local storage value
    isDevMode || isAMRevamp, // enables/disables local storage setter
  );

  useEffect(() => {
    return () => {
      if (isTestMode || !isAMRevamp) return;
      const entries = performance.getEntriesByType('navigation');
      const type = entries.length > 0 ? entries[0].type : 'other';

      // If the navigation type is not 'reload', it's a true unmount
      if (!isDevMode && isAMRevamp && type !== 'reload') {
        removeItem(localStorageKey);
      }
    };
  }, [isAMRevamp, isDevMode, localStorageKey, removeItem]);

  const updateMFEContext = useCallback(
    (newHostingId, newHostingData) => {
      if (!isAMRevamp) return;

      const {
        info: { addon_status: addonStatus = '' } = {},
        back_reference,
        expirationDate,
        prodCode,
        prodName,
        autoRenewFlag,
      } = newHostingData || {};

      setLSHostAppData((prevData) => ({
        ...prevData,
        hostingId: newHostingId,
        data: {
          ...prevData.data,
          ...(newHostingData && {
            hostingData: {
              ...prevData.data.hostingData,
              expiryDate: moment(expirationDate, YYYY_MMMM_DD_HMS, true).format(
                'MM/DD/YYYY',
              ),
              productInstanceId: back_reference,
              productCode: prodCode,
              productName: prodName,
              hasAutoRenew: autoRenewFlag,
              status: addonStatus.toLowerCase(),
            },
          }),
        },
        hostingStatus: {
          ...prevData.hostingStatus,
          renewalStatus: getHostingRenewalStatus(
            expirationDate,
            YYYY_MMMM_DD_HMS,
          ),
          isActive: addonStatus.toLowerCase() === 'active',
          isProvisioning: addonStatus.toLowerCase() === 'provisioning',
        },
      }));
      setBanners([]);
    },

    [isAMRevamp, setLSHostAppData],
  );

  // Only use local storage values if in dev mode or if revamp UI is enabled
  const mfeProviderData = useMemo(() => {
    if ((isDevMode || isAMRevamp) && lsHostAppData) {
      return {
        ...lsHostAppData,
        data: {
          ...lsHostAppData.data,
          hostingData: isAMRevamp
            ? { ...lsHostAppData?.data?.hostingData }
            : { ...providerData?.data?.hostingData },
        },
        callbacks: {
          ...providerData?.callbacks,
          ...(isDevMode && {
            refreshAccessToken: async () => lsHostAppData?.accessToken,
          }),
        },
      };
    }
    return providerData;
  }, [isAMRevamp, isDevMode, lsHostAppData, providerData]);

  const mfeContextValue = useMemo(
    () => ({
      ...mfeProviderData,
      accessToken,
      brand,
      brandFromJWT,
      banners,
      data: { ...mfeProviderData?.data, accessToken },
      updateMFEContext,
      setBanners,
    }),
    [
      accessToken,
      banners,
      brand,
      brandFromJWT,
      mfeProviderData,
      updateMFEContext,
    ],
  );

  return (
    <I18nextProvider i18n={i18n}>
      {!isTestMode && (
        <DevMode>
          {/* This tool allows you to change the bootstrap props values without needing to restart the server */}
          <MFEBootstrapTool
            formData={lsHostAppData}
            setFormData={setLSHostAppData}
            setAccessToken={setAccessToken}
          />
        </DevMode>
      )}
      <MFEContext.Provider
        value={mfeContextValue}
        style={{ backgroundColor: 'inherit' }}
      >
        <MUI theme={muiConfig?.theme} selector={muiConfig?.selector}>
          <ErrorBoundary
            onError={errorBoundaryConfig?.onError}
            ErrorScene={errorBoundaryConfig?.ErrorScene}
            hideOnError={errorBoundaryConfig?.hideOnError}
          >
            <HttpClient
              disabledReactQueryDevTools={
                reactQueryConfig?.disabledReactQueryDevTools
              }
              reactQueryDevToolsConfig={
                reactQueryConfig?.reactQueryDevToolsConfig
              }
              apiBaseUrl={mfeProviderData?.data?.apiBaseUrl}
              accessToken={
                isAMRevamp && !isDevMode
                  ? accessToken
                  : mfeProviderData?.accessToken
              }
              refreshAccessToken={
                mfeProviderData?.callbacks?.refreshAccessToken
              }
              setAccessToken={setAccessToken} // sets state
              appEnv={data?.appEnv}
            >
              <ReactSuspense>
                <ReactRouterDom basename={basename}>
                  <AnalyticsProvider enabled={hasAnalytics}>
                    {children}
                  </AnalyticsProvider>
                  <FullStoryPageChangeTracker />
                </ReactRouterDom>
              </ReactSuspense>
            </HttpClient>
          </ErrorBoundary>
        </MUI>
      </MFEContext.Provider>
    </I18nextProvider>
  );
};

MFEProvider.propTypes = propTypes;
export default MFEProvider;
